/**
 * Storage Service - Type-safe wrapper around localStorage and sessionStorage
 */

import type { TimelineRange, PeriodType, FavoritePage, SavedMACompSet } from '../types';
import { StorageKey } from '../types';

/**
 * Custom error class for storage operations
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public readonly key: string,
    public readonly cause?: Error
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

/**
 * Type-safe service for managing localStorage
 * Handles JSON serialization/deserialization and event listeners
 */
export class StorageService {
  private listeners: Map<string, Set<(e: StorageEvent) => void>> = new Map();

  /**
   * Get timeline range from storage
   * @returns TimelineRange object or null if not found
   */
  getTimelineRange(): TimelineRange | null {
    return this.getItem<TimelineRange>(StorageKey.TIMELINE_RANGE);
  }

  /**
   * Set timeline range in storage
   * @param range - TimelineRange to store
   */
  setTimelineRange(range: TimelineRange): void {
    this.setItem<TimelineRange>(StorageKey.TIMELINE_RANGE, range);
  }

  /**
   * Get period type from storage
   * @returns PeriodType or 'Annual' as default
   */
  getPeriodType(): PeriodType {
    return this.getItem<PeriodType>(StorageKey.PERIOD_TYPE) ?? 'Annual';
  }

  /**
   * Set period type in storage
   * @param type - PeriodType to store
   */
  setPeriodType(type: PeriodType): void {
    this.setItem<PeriodType>(StorageKey.PERIOD_TYPE, type);
  }

  /**
   * Get favorites from sessionStorage
   * @returns Array of FavoritePage objects or empty array if not found
   */
  getFavorites(): FavoritePage[] {
    return this.getSessionItem<FavoritePage[]>(StorageKey.FAVORITES) ?? [];
  }

  /**
   * Set favorites in sessionStorage
   * @param favorites - Array of FavoritePage objects to store
   */
  setFavorites(favorites: FavoritePage[]): void {
    this.setSessionItem<FavoritePage[]>(StorageKey.FAVORITES, favorites);
  }

  /**
   * Get selected M&A transaction IDs from sessionStorage
   * @returns Array of transaction IDs or empty array if not found
   */
  getSelectedMATransactions(): string[] {
    return this.getSessionItem<string[]>(StorageKey.SELECTED_MA_TRANSACTIONS) ?? [];
  }

  /**
   * Set selected M&A transaction IDs in sessionStorage
   * @param transactionIds - Array of transaction IDs to store
   */
  setSelectedMATransactions(transactionIds: string[]): void {
    this.setSessionItem<string[]>(StorageKey.SELECTED_MA_TRANSACTIONS, transactionIds);
  }

  /**
   * Add a transaction ID to selected M&A transactions
   * @param transactionId - Transaction ID to add
   */
  addMATransaction(transactionId: string): void {
    const current = this.getSelectedMATransactions();
    if (!current.includes(transactionId)) {
      current.push(transactionId);
      this.setSelectedMATransactions(current);
    }
  }

  /**
   * Remove a transaction ID from selected M&A transactions
   * @param transactionId - Transaction ID to remove
   */
  removeMATransaction(transactionId: string): void {
    const current = this.getSelectedMATransactions();
    const filtered = current.filter(id => id !== transactionId);
    this.setSelectedMATransactions(filtered);
  }

  /**
   * Check if a transaction is already selected
   * @param transactionId - Transaction ID to check
   * @returns true if transaction is selected
   */
  isMATransactionSelected(transactionId: string): boolean {
    const current = this.getSelectedMATransactions();
    return current.includes(transactionId);
  }

  /**
   * Clear all selected M&A transactions
   */
  clearMATransactions(): void {
    this.setSelectedMATransactions([]);
  }

  /**
   * Get saved M&A comp set from sessionStorage
   * @returns SavedMACompSet object or null if not found
   */
  getSavedMACompSet(): SavedMACompSet | null {
    return this.getSessionItem<SavedMACompSet>(StorageKey.SAVED_MA_COMP_SET);
  }

  /**
   * Set saved M&A comp set in sessionStorage
   * @param compSet - SavedMACompSet object to store
   */
  setSavedMACompSet(compSet: SavedMACompSet): void {
    this.setSessionItem<SavedMACompSet>(StorageKey.SAVED_MA_COMP_SET, compSet);
  }

  /**
   * Clear saved M&A comp set from sessionStorage
   */
  clearSavedMACompSet(): void {
    try {
      sessionStorage.removeItem(StorageKey.SAVED_MA_COMP_SET);
    } catch (error) {
      throw new StorageError(
        `Failed to clear saved MA comp set`,
        StorageKey.SAVED_MA_COMP_SET,
        error as Error
      );
    }
  }

  /**
   * Check if a saved M&A comp set exists
   * @returns true if comp set is saved
   */
  hasSavedMACompSet(): boolean {
    return this.getSavedMACompSet() !== null;
  }

  /**
   * Add a storage event listener for cross-tab synchronization
   * @param key - Storage key to listen for changes
   * @param callback - Callback function to execute on storage change
   */
  addStorageListener(key: StorageKey, callback: (e: StorageEvent) => void): void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }

    const listeners = this.listeners.get(key)!;
    listeners.add(callback);

    // Add the actual browser event listener if this is the first listener for this key
    if (listeners.size === 1) {
      window.addEventListener('storage', this.createStorageHandler(key));
    }
  }

  /**
   * Remove a storage event listener
   * @param key - Storage key to stop listening for
   * @param callback - Callback function to remove
   */
  removeStorageListener(key: StorageKey, callback: (e: StorageEvent) => void): void {
    const listeners = this.listeners.get(key);
    if (!listeners) return;

    listeners.delete(callback);

    // Remove the browser event listener if no more listeners for this key
    if (listeners.size === 0) {
      this.listeners.delete(key);
      window.removeEventListener('storage', this.createStorageHandler(key));
    }
  }

  /**
   * Remove all storage event listeners
   */
  removeAllListeners(): void {
    for (const key of this.listeners.keys()) {
      window.removeEventListener('storage', this.createStorageHandler(key as StorageKey));
    }
    this.listeners.clear();
  }

  /**
   * Clear a specific item from storage
   * @param key - Storage key to clear
   */
  clearItem(key: StorageKey): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      throw new StorageError(
        `Failed to clear item: ${key}`,
        key,
        error as Error
      );
    }
  }

  /**
   * Clear all items from storage
   */
  clearAll(): void {
    try {
      localStorage.clear();
    } catch (error) {
      throw new StorageError(
        'Failed to clear all storage',
        'all',
        error as Error
      );
    }
  }

  /**
   * Generic getter for typed localStorage access
   * @param key - Storage key
   * @returns Parsed value or null if not found or parse fails
   */
  private getItem<T>(key: StorageKey): T | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;

      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to parse stored value for key: ${key}`, error);
      return null;
    }
  }

  /**
   * Generic setter for typed localStorage access
   * @param key - Storage key
   * @param value - Value to store
   */
  private setItem<T>(key: StorageKey, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } catch (error) {
      throw new StorageError(
        `Failed to set item: ${key}`,
        key,
        error as Error
      );
    }
  }

  /**
   * Generic getter for typed sessionStorage access
   * @param key - Storage key
   * @returns Parsed value or null if not found or parse fails
   */
  private getSessionItem<T>(key: StorageKey): T | null {
    try {
      const item = sessionStorage.getItem(key);
      if (item === null) return null;

      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to parse stored value for key: ${key}`, error);
      return null;
    }
  }

  /**
   * Generic setter for typed sessionStorage access
   * @param key - Storage key
   * @param value - Value to store
   */
  private setSessionItem<T>(key: StorageKey, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      sessionStorage.setItem(key, serialized);
    } catch (error) {
      throw new StorageError(
        `Failed to set session item: ${key}`,
        key,
        error as Error
      );
    }
  }

  /**
   * Create a storage event handler for a specific key
   * @param key - Storage key to filter events for
   * @returns Event handler function
   */
  private createStorageHandler(key: StorageKey): (e: StorageEvent) => void {
    return (e: StorageEvent) => {
      // Only trigger callbacks if the changed key matches
      if (e.key === key) {
        const listeners = this.listeners.get(key);
        if (listeners) {
          listeners.forEach(callback => callback(e));
        }
      }
    };
  }

  /**
   * Check if localStorage is available
   * @returns true if localStorage is supported and accessible
   */
  static isAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }
}
